import { useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

/**
 * useOfflineSync
 *
 * Detecta estado offline do navegador e gerencia Background Sync para
 * mutações que falharam por falta de conexão (ex: registrar pagamento).
 *
 * Uso:
 *   const { isOffline, enqueueSyncPayments } = useOfflineSync();
 *
 *   // No onError de uma mutation:
 *   if (!navigator.onLine) enqueueSyncPayments();
 */
export function useOfflineSync() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOffline = () => setIsOffline(true);
    const handleOnline = () => setIsOffline(false);

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  /**
   * Mostra toast informativo e registra a tag de Background Sync no service
   * worker. Deve ser chamado quando uma mutação falha por ausência de rede.
   */
  const enqueueSyncPayments = useCallback(async () => {
    toast.warning(
      "Você está offline. O pagamento será sincronizado quando a conexão retornar.",
      { duration: 6000 }
    );

    try {
      const registration = await navigator.serviceWorker?.ready;
      // Background Sync API é progressivamente aprimorada — verifica suporte
      if (registration && "sync" in registration) {
        await (registration as ServiceWorkerRegistration & {
          sync: { register: (tag: string) => Promise<void> };
        }).sync.register("sync-payments");
      }
    } catch (err) {
      // Background Sync não suportado ou permissão negada — falha silenciosa
      console.warn("[useOfflineSync] Background Sync não disponível:", err);
    }
  }, []);

  return { isOffline, enqueueSyncPayments };
}

/**
 * Escuta o service worker avisar que reenviou escritas que estavam na fila.
 *
 * Sem isso, o reenvio acontecia em silêncio: o dado entrava no servidor e a
 * interface continuava mostrando o estado de antes, até a pessoa recarregar a
 * página por conta própria. Chamado uma vez, no layout.
 */
export function useSyncCompleteListener() {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handler = (event: MessageEvent) => {
      if (event.data?.type !== "gfi-sync-complete") return;
      const quantas = Number(event.data.count) || 0;
      if (quantas <= 0) return;

      // O que foi reenviado pode ter mudado qualquer tela: revalida tudo.
      void queryClient.invalidateQueries();
      toast.success(
        quantas === 1
          ? "A ação que ficou pendente foi enviada."
          : `${quantas} ações que ficaram pendentes foram enviadas.`,
      );
    };

    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [queryClient]);
}
